// ============================================================================
// StudentOnboardingPage - Unified onboarding hub for new students
// Handles: Placement Test → Classroom Assignment → Ready to Learn
// ============================================================================

import React, { useState } from "react";
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  Stack,
  useTheme,
  alpha,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  CheckCircle as CheckIcon,
  HourglassEmpty as PendingIcon,
  School as SchoolIcon,
  Assignment as TestIcon,
  Class as ClassroomIcon,
  PlayArrow as StartIcon,
  Refresh as RefreshIcon,
  Logout as LogoutIcon,
  Email as EmailIcon,
  AccessTime as TimeIcon,
  TipsAndUpdates as TipIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { getUserProfile } from "../../services/userService";
import { StudentProfile } from "../../utils/studentUtils";
import { useSnackbar } from "notistack";

// ============================================================================
// Types
// ============================================================================

type OnboardingStep = "account" | "placement" | "assignment" | "ready";

interface StepStatus {
  completed: boolean;
  current: boolean;
  label: string;
  description: string;
}

// ============================================================================
// Styled Components
// ============================================================================

const PageContainer = styled(Container)(({ theme }) => ({
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(4),
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${
          theme.palette.background.default
        } 0%, ${alpha(theme.palette.primary.dark, 0.1)} 100%)`
      : `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, ${alpha(
          theme.palette.primary.light,
          0.05
        )} 100%)`,
}));

const OnboardingCard = styled(Paper)(({ theme }) => ({
  width: "100%",
  maxWidth: 700,
  borderRadius: theme.spacing(2),
  overflow: "hidden",
  boxShadow: theme.shadows[8],
}));

const HeaderSection = styled(Box)(({ theme }) => ({
  background:
    theme.palette.mode === "dark"
      ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
      : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
  color: theme.palette.primary.contrastText,
  padding: theme.spacing(4),
  textAlign: "center",
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
}));

const ActionCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(3),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: theme.spacing(1.5),
  background:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.primary.main, 0.08)
      : alpha(theme.palette.primary.main, 0.04),
}));

const InfoCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(2),
  borderRadius: theme.spacing(1),
  background: theme.palette.background.paper,
  border: `1px solid ${theme.palette.divider}`,
}));

const StyledStepper = styled(Stepper)(({ theme }) => ({
  "& .MuiStepLabel-root": {
    padding: theme.spacing(1, 0),
  },
  "& .MuiStepLabel-label": {
    fontWeight: 500,
  },
  "& .MuiStepLabel-label.Mui-completed": {
    color: theme.palette.success.main,
    fontWeight: 600,
  },
  "& .MuiStepLabel-label.Mui-active": {
    color: theme.palette.primary.main,
    fontWeight: 600,
  },
  "& .MuiStepIcon-root.Mui-completed": {
    color: theme.palette.success.main,
  },
  "& .MuiStepIcon-root.Mui-active": {
    color: theme.palette.primary.main,
  },
}));

const FooterActions = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(3),
  paddingTop: theme.spacing(3),
  borderTop: `1px solid ${theme.palette.divider}`,
}));

// ============================================================================
// Helper Functions
// ============================================================================

const getActiveStep = (
  hasPlacementTest: boolean,
  hasClassroom: boolean
): number => {
  if (!hasPlacementTest) return 1; // On placement test step
  if (!hasClassroom) return 2; // On assignment step
  return 3; // Ready
};

const getStepIcon = (
  step: number,
  activeStep: number,
  theme: any
): React.ReactNode => {
  const isCompleted = step < activeStep;
  const isCurrent = step === activeStep;

  if (isCompleted) {
    return <CheckIcon sx={{ color: theme.palette.success.main }} />;
  }
  if (isCurrent) {
    return <PendingIcon sx={{ color: theme.palette.primary.main }} />;
  }
  return undefined;
};

// ============================================================================
// Component
// ============================================================================

const StudentOnboardingPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, setUser, signOut } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isRefreshing, setIsRefreshing] = useState(false);

  const profile = user as StudentProfile | null;
  const studentName = profile?.firstName || "Student";

  // Determine states
  const hasPlacementTest = Boolean(profile?.placementTest?.result);
  const hasClassroom = Boolean(profile?.myClassroom);
  const placementResult = profile?.placementTest?.result;

  const activeStep = getActiveStep(hasPlacementTest, hasClassroom);

  // Step definitions
  const steps = [
    {
      label: "Create Account",
      description: "Your account is active and verified",
      completed: true,
    },
    {
      label: "Placement Test",
      description: hasPlacementTest
        ? `Completed - Recommended: ${placementResult?.programName}`
        : "Take a short assessment to find your level",
      completed: hasPlacementTest,
    },
    {
      label: "Classroom Assignment",
      description: hasClassroom
        ? `Enrolled in ${profile?.myClassroom?.programName}`
        : "Your teacher will assign you to a classroom",
      completed: hasClassroom,
    },
    {
      label: "Start Learning",
      description: "Access your course materials and quizzes",
      completed: false,
    },
  ];

  // Handlers
  const handleTakePlacementTest = () => {
    navigate("/student/placement-test");
  };

  const handleGoToClassroom = () => {
    navigate("/student/my-classroom");
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      const response = await getUserProfile();
      const updatedProfile = response?.data;

      if (updatedProfile) {
        setUser(updatedProfile);

        if (updatedProfile.myClassroom) {
          enqueueSnackbar("You have been assigned to a classroom!", {
            variant: "success",
          });
          // Small delay to show the success message
          setTimeout(() => {
            navigate("/student/my-classroom");
          }, 1000);
        } else if (!hasPlacementTest && updatedProfile.placementTest?.result) {
          enqueueSnackbar("Placement test completed! Waiting for assignment.", {
            variant: "success",
          });
        } else {
          enqueueSnackbar("No updates yet. Check back later.", {
            variant: "info",
          });
        }
      }
    } catch (error) {
      enqueueSnackbar("Failed to refresh. Please try again.", {
        variant: "error",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleLogout = async () => {
    await signOut();
  };

  // ============================================================================
  // Render: Primary Action Card (dynamic based on state)
  // ============================================================================

  const renderActionCard = () => {
    // State 1: Needs to take placement test
    if (!hasPlacementTest) {
      return (
        <ActionCard elevation={0}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <TestIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  Take Your Placement Test
                </Typography>
              </Box>

              <Typography variant="body2" color="text.secondary">
                Complete a short assessment so we can place you in the right
                program level. This helps us tailor your learning experience to
                your current English proficiency.
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  color: "text.secondary",
                }}
              >
                <TimeIcon fontSize="small" />
                <Typography variant="caption">
                  Approximately 15-20 minutes
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                startIcon={<StartIcon />}
                onClick={handleTakePlacementTest}
                sx={{
                  mt: 1,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                }}
                fullWidth
              >
                Start Placement Test
              </Button>
            </Stack>
          </CardContent>
        </ActionCard>
      );
    }

    // State 2: Waiting for classroom assignment
    if (!hasClassroom) {
      return (
        <ActionCard elevation={0}>
          <CardContent sx={{ p: 3 }}>
            <Stack spacing={2}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                <ClassroomIcon color="primary" sx={{ fontSize: 28 }} />
                <Typography variant="h6" fontWeight={600}>
                  Waiting for Classroom Assignment
                </Typography>
              </Box>

              <Alert severity="success" sx={{ borderRadius: 1 }}>
                <Typography variant="body2">
                  <strong>Placement Complete!</strong> Your recommended level:{" "}
                  <Chip
                    label={
                      placementResult ? placementResult.programName : "Pending"
                    }
                    size="small"
                    color="primary"
                    sx={{ ml: 0.5, fontWeight: 600 }}
                  />
                </Typography>
              </Alert>

              <Typography variant="body2" color="text.secondary">
                Your teacher will review your results and assign you to a
                classroom. This typically happens within 24-48 hours.
              </Typography>

              <Button
                variant="contained"
                size="large"
                startIcon={
                  isRefreshing ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={handleRefresh}
                disabled={isRefreshing}
                sx={{
                  mt: 1,
                  py: 1.5,
                  fontWeight: 600,
                  borderRadius: 2,
                }}
                fullWidth
              >
                {isRefreshing ? "Checking..." : "Check for Assignment"}
              </Button>
            </Stack>
          </CardContent>
        </ActionCard>
      );
    }

    // State 3: Ready to go (shouldn't normally see this, but just in case)
    return (
      <ActionCard elevation={0}>
        <CardContent sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <SchoolIcon color="success" sx={{ fontSize: 28 }} />
              <Typography variant="h6" fontWeight={600} color="success.main">
                You're All Set!
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary">
              You've been assigned to{" "}
              <strong>{profile?.myClassroom?.programName}</strong>. Start your
              learning journey now!
            </Typography>

            <Button
              variant="contained"
              color="success"
              size="large"
              startIcon={<StartIcon />}
              onClick={handleGoToClassroom}
              sx={{
                mt: 1,
                py: 1.5,
                fontWeight: 600,
                borderRadius: 2,
              }}
              fullWidth
            >
              Go to My Classroom
            </Button>
          </Stack>
        </CardContent>
      </ActionCard>
    );
  };

  // ============================================================================
  // Render: Tips/Info Section
  // ============================================================================

  const renderInfoSection = () => {
    if (!hasPlacementTest) {
      return (
        <InfoCard elevation={0}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <TipIcon color="info" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Tips for the Placement Test
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Find a quiet place with stable internet
                  <br />
                  • Answer honestly - there's no passing or failing
                  <br />
                  • The test adapts to your level automatically
                  <br />• You can't retake it, so take your time
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </InfoCard>
      );
    }

    if (!hasClassroom) {
      return (
        <InfoCard elevation={0}>
          <CardContent sx={{ p: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
              <EmailIcon color="info" sx={{ mt: 0.25 }} />
              <Box>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  What Happens Next?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Your teacher will be notified of your results
                  <br />
                  • You'll be assigned to a classroom (Yellow, Green, or Access)
                  <br />
                  • We'll email you when your classroom is ready
                  <br />• Check back here anytime using the button above
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </InfoCard>
      );
    }

    return null;
  };

  // ============================================================================
  // Main Render
  // ============================================================================

  return (
    <PageContainer maxWidth={false}>
      <OnboardingCard elevation={8}>
        {/* Header */}
        <HeaderSection>
          <SchoolIcon sx={{ fontSize: 48, mb: 1.5, opacity: 0.9 }} />
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Welcome, {studentName}!
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            Let's get you started on your English learning journey
          </Typography>
        </HeaderSection>

        {/* Content */}
        <ContentSection>
          {/* Progress Stepper */}
          <StyledStepper activeStep={activeStep} orientation="vertical">
            {steps.map((step, index) => (
              <Step key={step.label} completed={step.completed}>
                <StepLabel
                  optional={
                    <Typography variant="caption" color="text.secondary">
                      {step.description}
                    </Typography>
                  }
                  StepIconComponent={
                    step.completed
                      ? () => (
                          <CheckIcon
                            sx={{
                              color: theme.palette.success.main,
                              fontSize: 24,
                            }}
                          />
                        )
                      : undefined
                  }
                >
                  {step.label}
                </StepLabel>
              </Step>
            ))}
          </StyledStepper>

          {/* Primary Action */}
          {renderActionCard()}

          {/* Info/Tips */}
          {renderInfoSection()}

          {/* Footer Actions */}
          <FooterActions>
            <Button
              variant="text"
              color="inherit"
              startIcon={<LogoutIcon />}
              onClick={handleLogout}
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "text.primary",
                  backgroundColor: alpha(theme.palette.action.hover, 0.5),
                },
              }}
            >
              Sign Out
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              textAlign="center"
            >
              Having trouble? Contact your institution or email{" "}
              <Box
                component="a"
                href="mailto:support@eapspace.com"
                sx={{ color: "primary.main", textDecoration: "none" }}
              >
                support@eapspace.com
              </Box>
            </Typography>
          </FooterActions>
        </ContentSection>
      </OnboardingCard>
    </PageContainer>
  );
};

export default StudentOnboardingPage;
